import React, { useEffect } from 'react';
import { Drawer, Form, Input, Select, Button, Divider, Spin } from 'antd';
import { AppstoreOutlined, CloseOutlined, FolderOpenOutlined, TagOutlined, LoadingOutlined } from '@ant-design/icons';
import { categoryType } from '../../actions/constants/constant';

export type CategoryStatus = 'ENABLED' | 'DISABLED';

export interface QueryCategoryFormValues {
  name: string;
  description?: string;
  status: CategoryStatus;
}

interface QueryCategoryFormModalProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onFinish: (values: QueryCategoryFormValues) => void;
  form: any;
  initialValues?: Partial<QueryCategoryFormValues>;
  isLoading?: boolean;   // ← add
}

const CategoryFormModal: React.FC<QueryCategoryFormModalProps> = ({
  open,
  title,
  onCancel,
  onFinish,
  form,
  initialValues,
  isLoading,             // ← add
}) => {

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.setFieldsValue({ status: 'ENABLED' });
      }
    }
  }, [open, initialValues, form]);

  return (
    <Drawer
      className="svc-drawer"
      open={open}
      onClose={onCancel}
      size={470}
      closable={false}
      destroyOnClose
      title={null}
      footer={
        <div className="svc-drawer-footer">
          <Button className="svc-btn-cancel" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="svc-btn-save"
            type="primary"
            onClick={() => form.submit()}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Category'}
          </Button>
        </div>
      }
    >
      {/* ── Spinner Overlay ── */}
      {isLoading && (
        <div className="svc-spinner-overlay">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
          />
          <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
            Saving category...
          </span>
        </div>
      )}

      {/* ── Sticky Header ── */}
      <div className="svc-drawer-header">
        <div className="svc-drawer-header-inner">
          <div className="svc-drawer-title-group">
            <div className="svc-drawer-icon-box">
              <FolderOpenOutlined />
            </div>
            <div className="svc-drawer-title-text">
              <span className="svc-drawer-title">{title}</span>
              <span className="svc-drawer-subtitle">Fill in the details to create or update a category</span>
            </div>
          </div>
          <button className="svc-drawer-close-btn" onClick={onCancel} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
        <Divider className="svc-drawer-header-divider" />
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} className="svc-form">

        {/* ══ Section: Basic Information ══ */}
        <div className="svc-form-section svc-form-section--last">
         
          <Form.Item
            name="categoryName"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
            className="svc-form-item"
          >
            <Input className="svc-input" placeholder="e.g. Light Motor Vehicle" />
          </Form.Item>

          <Form.Item
            name="categoryType"
            label="Category Type"
            rules={[{ required: true, message: 'Please select category type' }]}
            className="svc-form-item"
          >
            <Select className="svc-select" placeholder="Select category type">
              {categoryType.map((g: string) => (
                <Select.Option key={g} value={g}>{g}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="shortName"
            label="Category Code"
            rules={[
              { required: true, message: 'Please enter category code' },
              { max: 10, message: 'Maximum 10 characters allowed' },
            ]}
            className="svc-form-item"
          >
            <Input className="svc-input" placeholder="e.g. CAT01" maxLength={10} />
          </Form.Item>
        </div>

      </Form>
    </Drawer>
  );
};

export default CategoryFormModal;